import { API, ApiPrefixes } from "../api";

const branchWalletBase = ApiPrefixes.branchWallet;

const normalizeLedgerPaginated = (response) => {
  const inner = response?.data;
  const rows = Array.isArray(inner?.data) ? inner.data : [];
  return {
    rows,
    pagination: {
      currentPage: inner?.current_page ?? response?.currentPage ?? 1,
      lastPage: inner?.last_page ?? response?.lastPage ?? 1,
      total: inner?.total ?? response?.total ?? 0,
      perPage: inner?.per_page ?? response?.perPage ?? 20,
      totalPages: inner?.last_page ?? response?.lastPage ?? 1,
    },
  };
};

export const branchWalletApi = API.injectEndpoints({
  endpoints: (builder) => ({
    getBranchWalletBalance: builder.query({
      query: () => ({
        url: `${branchWalletBase}/balance?guard=staff`,
      }),
      transformResponse: (res) => {
        const d = res?.data ?? {};
        const balanceNgn = Number(d.balance_ngn ?? 0);
        const pendingWd =
          d.pending_withdrawals_total_ngn != null ? Number(d.pending_withdrawals_total_ngn) : 0;
        let available =
          d.available_to_withdraw_ngn != null ? Number(d.available_to_withdraw_ngn) : null;
        if (available == null || Number.isNaN(available)) {
          available = Math.max(0, Math.round((balanceNgn - pendingWd) * 100) / 100);
        }
        return {
          balance_ngn: balanceNgn,
          pending_recovery_ngn: d.pending_recovery_ngn ?? 0,
          pending_withdrawals_total_ngn: pendingWd,
          available_to_withdraw_ngn: available,
        };
      },
      providesTags: [{ type: "branch-wallet-balance", id: "CURRENT" }],
    }),

    getBranchWalletWithdrawals: builder.query({
      query: ({ page = 1, per_page = 20 } = {}) => {
        const params = new URLSearchParams({
          guard: "staff",
          page: String(page),
          per_page: String(per_page),
        });
        return {
          url: `${branchWalletBase}/withdrawals?${params.toString()}`,
        };
      },
      transformResponse: (response) => normalizeLedgerPaginated(response),
      providesTags: [{ type: "branch-wallet-withdrawals", id: "LIST" }],
    }),

    getBranchWalletLedger: builder.query({
      query: ({ page = 1, per_page = 20, from_date, to_date } = {}) => {
        const params = new URLSearchParams({
          guard: "staff",
          page: String(page),
          per_page: String(per_page),
        });
        if (from_date) params.append("from_date", from_date);
        if (to_date) params.append("to_date", to_date);
        return {
          url: `${branchWalletBase}/ledger?${params.toString()}`,
        };
      },
      transformResponse: (response) => normalizeLedgerPaginated(response),
      providesTags: [{ type: "branch-wallet-ledger", id: "LIST" }],
    }),

    getBranchWalletBankAccounts: builder.query({
      query: () => ({
        url: `${branchWalletBase}/bank_accounts?guard=staff`,
      }),
      transformResponse: (res) => (Array.isArray(res?.data) ? res.data : []),
      providesTags: [{ type: "branch-wallet-balance", id: "BANKS" }],
    }),

    requestBranchWalletWithdrawal: builder.mutation({
      query: (body) => ({
        url: `${branchWalletBase}/withdrawals?guard=staff`,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "branch-wallet-balance", id: "CURRENT" },
        { type: "branch-wallet-ledger", id: "LIST" },
        { type: "branch-wallet-withdrawals", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetBranchWalletBalanceQuery,
  useGetBranchWalletWithdrawalsQuery,
  useGetBranchWalletLedgerQuery,
  useGetBranchWalletBankAccountsQuery,
  useRequestBranchWalletWithdrawalMutation,
} = branchWalletApi;
